import { debounce } from 'underscore';

import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';

import { SubtitleType, VideogularComponent } from './videogular/Videogular.Component';
import { VbBaseMenuComponent } from './shared/VbBaseMenu.Component';
import { PLAYER_USER_PREFERENCE_KEY } from 'vbrick-player-src/shared/userPreference/PlayerUserPreferenceHandler';
import { PlayerUserPreferencesService } from 'vbrick-player-src/shared/userPreference/PlayerUserPreference.Service';

import { IAudioTrack } from './IAudioTrack';
import { ISubtitle } from './ISubtitle';
import { ISubtitleOption } from './ISubtitleOption';
import { IVbSource } from './IVbSource';
import { IVbTextTrack, TextTrackHelperService } from './TextTrackHelper.Service';
import { LanguageNames, RIGHT_TO_LEFT_LANGUAGE_CODES } from './LanguageNames';
import { SubtitleService } from './Subtitle.Service';
import { TextTrackKinds } from './TextTrackKinds';
import { TextTrackModes } from './TextTrackModes';
import { VgStates } from './videogular/VgStates';
import { userAgentUtil } from './UserAgentUtil';

import styles from './VbSubtitleMenuButton.module.less';

export const SUBTITLE_OPTION_CAPTIONS: string = 'captions';

/**
 * TODO - This compoonent needs to be refactored to be more simple and readable.
 */

@Component({
	selector: 'vb-subtitle-menu-button',
	host: {
		'[class]': 'styles.root'
	},
	template: `
		<div
			[ngClass]="[styles.dropdown, styles.dropdownWrapper]"
			dropdown
			#dropdown="bs-dropdown"
			[dropup]="true"
			[hidden]="!isCaptionsAvailable && !subtitleOptionsInternal.length && (!audioTracks.length || audioTracks?.length == 1)">

			<button
				type="button"
				id="buttonCCMenu"
								dropdownToggle
				[attr.aria-label]="translations.audioSubtitlesAndCCLabel"
				[tooltip]="translations.audioSubtitlesAndCCLabel"
				placement="top"
				[ngClass]="styles.button"
				vbControlBarFocus>
			</button>

			<div
				[ngClass]="[
					styles.dropdownMenu,
					styles.menuWrapper,
					isSmallSizePlayer ? styles.smallSizePlayer : ''
				]"
				*dropdownMenu
				id="dropdownCCMenu"
				role="menu"
				aria-labelledby="buttonCCMenu"
				#vbDropdownMenu
				tabindex="0">
				<vb-audio-menu
					[hidden]="!audioTracks?.length || audioTracks?.length === 1"
					[audioTracks]="audioTracks"
					[selectedCssClass]="styles.selected"
					[translations]="translations">
				</vb-audio-menu>
				<ul *ngIf="isCaptionsAvailable || subtitleOptionsInternal?.length"
					class="playerSettingsSubmenu"
					role="menu"
					[ngClass]="styles.rightSubMenu">
					<li role="none" [ngClass]="styles.subMenuHeading">{{ translations.subtitles }}</li>
					<li
						*ngIf="isCaptionsAvailable"
						role="none">
						<button type="button"
							role="menuitem"
							class="dropdown-item btn-link"
							(click)="onManualLangChange(SUBTITLE_OPTION_CAPTIONS)"
							[ngClass]="captionsEnabled ? styles.selected : ''">
							{{ translations.closedCaptions }}
						</button>
					</li>
					<li
						*ngFor="let subtitleOption of subtitleOptionsInternal"
						role="none">
						<button type="button"
							role="menuitem"
							class="dropdown-item btn-link"
							(click)="onManualLangChange(subtitleOption);"
							[ngClass]="vgApi.activeSubtitle === subtitleOption || (vgApi.activeSubtitle && subtitleOption.mode === 'showing') ? styles.selected : ''">
							{{ subtitleOption.displayLabel }}
						</button>
					</li>
					<li role="none">
						<button type="button"
							role="menuitem"
							class="dropdown-item btn-link"
							(click)="onManualLangChange(undefined)"
							[ngClass]="!vgApi.activeSubtitle && !captionsEnabled ? styles.selected : ''">
							{{translations.off}}
						</button>
					</li>
				</ul>
			</div>
		</div>`,
    standalone: false
})
export class VbSubtitleMenuButtonComponent extends VbBaseMenuComponent implements OnChanges, OnDestroy, OnInit {
	@Input() public activeSource: IVbSource;
	@Input() public currentState: VgStates;
	@Input() public currentTime: number;
	@Input() public isCaptionsAvailable: boolean;
	@Input() public subtitleOptions: ISubtitleOption[];
	@Input('subtitleLangPreferenceOrder') public langOrder: string[];
	@Input() public forcedCCOn: boolean;
	@Input() public translations: any;

	public readonly styles = { ...this.menuButtonStyles, ...styles };
	public readonly SUBTITLE_OPTION_CAPTIONS = SUBTITLE_OPTION_CAPTIONS;

	public isMobile: boolean;
	public subtitleOptionsInternal: ISubtitleOption[];
	public nativeSubtitles: TextTrack[];
	public captionsEnabled: boolean;

	private isSafari: boolean;
	private isTextTrackType: boolean;
	private onTextTrackListChangeDebounce: () => void;
	private subtitles: ISubtitle[];
	private subscription: Subscription = new Subscription();
	private selectedNativeTextTrack: TextTrack;
	private isManualChanged: boolean;

	public audioTracks: IAudioTrack[] = [];

	constructor(
		private SubtitleService: SubtitleService,
		private TextTrackHelper: TextTrackHelperService,
		private readonly PlayerUserPreferencesService: PlayerUserPreferencesService,
		public vgApi: VideogularComponent
	) {
		super();
	}

	public ngOnInit(): void {
		this.captionsEnabled = false;
		this.isMobile = userAgentUtil.isMobile;
		this.isSafari = userAgentUtil.isSafari;
		this.onTextTrackListChangeDebounce = debounce(() => this.onTextTrackListChange(), 2000); //may get multiple. plus there's a bit of a delay.

		if (this.isSafari) {
			this.initSafariCaptions();
		}

		this.subscription.add(
			this.vgApi.textTracks$.subscribe(result => {
			this.nativeSubtitles = (result || []).filter(track =>
				track.kind === TextTrackKinds.SUBTITLES && track.label !== this.translations.closedCaptions && !track.vb);
			this.updateSubtitlesCollection();
		}));
		this.subscription.add(
			this.vgApi.audioTracks$.subscribe((audioTracks: IAudioTrack[]) => {
				this.audioTracks = audioTracks.map(audio => {
					audio.displayLabel = LanguageNames[audio.lang.toLowerCase()] || audio.name;
					return audio;
				});
				this.sortSubtitlesByAudioTrackOrder();
			})
		);
		this.onAutoForcedLangSelect();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!this.vgApi.mediaElement) {
			return;
		}

		if (changes.activeSource) {
			this.onActiveSourceChange();
		}

		if (changes.isCaptionsAvailable) {
			this.onIsCaptionsAvailableChange();
		}

		if (changes.subtitleOptions) {
			this.subtitleOptions = this.subtitleOptions?.map(subtitle => {
				const langCode = subtitle.language?.toLowerCase();
				subtitle.isRTL = RIGHT_TO_LEFT_LANGUAGE_CODES.includes(langCode);
				return subtitle;
			});
			this.onSubtitleOptionsChange();
		}

		if (changes.currentTime) {
			this.onCurrentTimeChange();
		}

		if ((userAgentUtil.isChrome || userAgentUtil.isEdge) && this.currentState && changes.currentState) {
			this.onPlayerStateChange();
		}
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.subscription?.unsubscribe();
	}

	/**
	 * solving Latest chrome version(86) and Chromium edge issue. Once video is stopped
	 * the display subtitle get stuck to player and keep showing after play start.
	 * Hiding subtitle when video is paused/stopped and showing it again when it is played again.
	 */
	private onPlayerStateChange(): void {
		if (!this.vgApi || this.vgApi.isForceHtmlSubtitleRendering) {
			return;
		}

		if (this.currentState !== VgStates.PLAY) {
			this.TextTrackHelper.hideAllTracks(this.vgApi.rawMediaElement);
		} else {
			const activeSubtitle: ISubtitleOption = this.vgApi.activeSubtitle as ISubtitleOption;

			if (activeSubtitle) {
				 if (this.captionsEnabled) {
					this.setCaptionEnabled();
					return;
				 }

				 const textTrack: IVbTextTrack = Array.from(this.vgApi.rawMediaElement.textTracks)
					.find(track => track.label === activeSubtitle.label);

				if (textTrack) {
					textTrack.mode = TextTrackModes.SHOWING;
				}
			}
		}
	}

	private findCurrentSubtitle(currentTime: number): string {
		const currentTimeSec: number = currentTime / 1000; //ms to sec
		let currentSubtitleFindResult: any;

		if (this.subtitles) {
			currentSubtitleFindResult = this.subtitles.find(marker => marker.start <= currentTimeSec && marker.end > currentTimeSec);
		} else if (this.selectedNativeTextTrack) {
			currentSubtitleFindResult = Array.from(this.selectedNativeTextTrack.activeCues || []).find(cue => cue.startTime <= currentTimeSec && cue.endTime > currentTimeSec);
		}

		return currentSubtitleFindResult ? currentSubtitleFindResult.text : null;
	}

	private hideSubtitles(): void {
		this.subtitles = null;
		this.selectedNativeTextTrack = null;
		this.vgApi.setSubtitleTrackInHlsPlugin?.(false, null);

		if (!this.vgApi.isHtmlSubtitleRenderingEnabled) {
			this.TextTrackHelper.hideAllTracks(this.vgApi.rawMediaElement);
		}
	}

	private initSafariCaptions(): void {
		this.vgApi.rawMediaElement.textTracks.addEventListener('addtrack', event => {
			const track: TextTrack = event.track as TextTrack;
			const isSafariAutoCaptionTrack: boolean = track.label === '' && track.kind === TextTrackKinds.CAPTIONS;

			//Hide the auto-added CC track, if captions hasn't been selected. Safari shows it by default.
			if (isSafariAutoCaptionTrack && this.vgApi.activeSubtitle !== SUBTITLE_OPTION_CAPTIONS) {
				track.mode = TextTrackModes.HIDDEN;
			}
		});
	}

	private isNativeVideoPlayback(): boolean {
		return !!this.vgApi.rawMediaElement.textTracks;
	}

	private onActiveSourceChange(): void {
		this.reevaluateSubtitleRenderingMethod();

		if (!this.vgApi.isHtmlSubtitleRenderingEnabled && this.vgApi.activeSubtitle) {
			this.renderThroughTextTrack();
		}

		this.onAutoForcedLangSelect();
	}

	private onCurrentTimeChange(): void {
		if (this.vgApi.activeSubtitle && this.vgApi?.isHtmlSubtitleRenderingEnabled && typeof this.vgApi.currentTime === 'number' && this.vgApi.currentTime >= 0) {
			this.vgApi.currentSubtitle = this.findCurrentSubtitle(this.vgApi.currentTime);
		} else {
			this.vgApi.currentSubtitle = null;
		}
	}

	private onIsCaptionsAvailableChange(): void {
		this.vgApi.isCaptionsAvailable = this.isCaptionsAvailable;
	}

	private onSubtitleOptionsChange(): void {
		const userPreferenceSubtitle = this.PlayerUserPreferencesService.getPreference<ISubtitleOption>(PLAYER_USER_PREFERENCE_KEY.SUBTITLE);
		const userPreferenceSubtitleOption = this.subtitleOptions?.find(subtitle => subtitle.language === userPreferenceSubtitle?.language);

		//This is needed when user changes subtitle text in pulse menu and expects player to reload the subtitle.
		const { src, language } = (this.vgApi.activeSubtitle ?? {}) as ISubtitleOption;
		const newSubtitleOption = this.subtitleOptions?.find(subtitle => subtitle.language === language);
		const activeSourceFileChanged = !!src && !!newSubtitleOption && src !== newSubtitleOption?.src;
		const selectedSubtitle = activeSourceFileChanged ? newSubtitleOption : userPreferenceSubtitleOption;

		if (activeSourceFileChanged || !!userPreferenceSubtitleOption) {
			this.vgApi.setActiveSubtitle(selectedSubtitle);
			this.showSubtitles();
		} else {
			this.hideSubtitles();
			this.vgApi.setActiveSubtitle(null);
			this.vgApi.currentSubtitle = null;
		}
		this.updateSubtitlesCollection();
	}

	private updateSubtitlesCollection(): void {
		this.nativeSubtitles = this.nativeSubtitles || [];
		this.subtitleOptions = this.subtitleOptions || [];

		this.subtitleOptionsInternal = [...this.subtitleOptions, ...this.nativeSubtitles];

		this.subtitleOptionsInternal = this.subtitleOptionsInternal
			.map(subtitle => {
				const langCode = subtitle.language?.toLowerCase();
				subtitle.displayLabel = LanguageNames[langCode] || subtitle.label;
				subtitle.isRTL = RIGHT_TO_LEFT_LANGUAGE_CODES.includes(langCode);
				return subtitle;
			})
			.filter(Boolean);
		const activeSubtitle = this.subtitleOptionsInternal.find(subtitle => (subtitle as TextTrack).mode === TextTrackModes.SHOWING);
		this.sortSubtitlesByAudioTrackOrder();

		// if by default any subtitle is enabled in playlist file.
		if (activeSubtitle) {
			this.vgApi.setActiveSubtitle(activeSubtitle);
		}
	}

	private sortSubtitlesByAudioTrackOrder(): void {
		if (this.subtitleOptionsInternal?.length && this.audioTracks?.length) {
			this.subtitleOptionsInternal = this.subtitleOptionsInternal.sort((a, b) => {
				const langA = a.language.toLowerCase();
				const langB = b.language.toLowerCase();
				const audioLangAIndex = this.audioTracks.findIndex(track => track.lang.toLowerCase() === langA);
				const audioLangBIndex = this.audioTracks.findIndex(track => track.lang.toLowerCase() === langB);
				if (audioLangAIndex !== -1 && audioLangBIndex === -1) return -1;
				if (audioLangAIndex === -1 && audioLangBIndex !== -1) return 1;
				if (audioLangAIndex !== -1 && audioLangBIndex !== -1) return audioLangAIndex - audioLangBIndex;
				return 0; // Leave unsorted if neither is found
			});
		}
	}

	private onTextTrackListChange(): void {
		const textTracks: TextTrackList | any[] = this.vgApi.rawMediaElement.textTracks || [];

		//find the subtitle being shown, if any
		const showingTrack: IVbTextTrack = Array.from(textTracks as TextTrackList).find(currentTextTrack => currentTextTrack.mode === TextTrackModes.SHOWING);
		const showingLabel: string = showingTrack && showingTrack.label;

		//update the activeSubtitle value to match the change
		this.vgApi.setActiveSubtitle(
			typeof showingLabel === 'string' ? (
				showingTrack.vb ? this.subtitleOptions.find(option => option.label === showingLabel) :
				this.captionsEnabled ? SUBTITLE_OPTION_CAPTIONS :
				showingTrack
			) : null);
	}

	private reevaluateSubtitleRenderingMethod(): void {
		const is360Enabled: boolean = this.vgApi.activeSource && this.vgApi.activeSource.is360Enabled;

		this.vgApi.isHtmlSubtitleRenderingEnabled =
			// check if 360 video is enabled
			is360Enabled ||

			// workaround Safari Mac text track bug where its Auto default selection breaks the TextTrack API surfacing of tracks
			userAgentUtil.isSafariMac ||

			userAgentUtil.isFirefox ||

			// IE and legacy Edge have only basic support for VTT. Can't style, so you're stuck with what is configured through the browser's accessibility settings (defaults are questionable)
			userAgentUtil.isIeOrLegacyEdge ||

			this.vgApi.isForceHtmlSubtitleRendering ||

			!this.TextTrackHelper.isNativeTextTrackSupport ||

			!this.isNativeVideoPlayback();

		this.vgApi.disableNativeTextTrackRenderInHlsPlugin?.(this.vgApi.isHtmlSubtitleRenderingEnabled);
	}

	private renderThroughTextTrack(): void {
		const activeSubtitle: ISubtitleOption = this.vgApi.activeSubtitle as ISubtitleOption;

		//attempt to locate an existing track. If it doesn't already exist, create a new track
		const textTrack: IVbTextTrack = this.TextTrackHelper.getOrCreateTrack(this.vgApi.rawMediaElement as HTMLVideoElement, TextTrackKinds.CAPTIONS, activeSubtitle.label);

		//frustratingly the TextTrack spec deliberately leaves out track removal (they were only thinking about in-band tracks and not dynamic), so we have to reuse
		//remove all cues
		this.TextTrackHelper.clearCues(textTrack);

		//add cues to the track
		this.subtitles?.forEach(subtitle => {
			const cue = this.TextTrackHelper.addCue(textTrack, subtitle.start, subtitle.end, subtitle.text);
		});

		//surface the text track
		textTrack.mode = TextTrackModes.SHOWING;
	}

	private setSafariCaptionsEnabled(isEnabled: boolean): void {
		const track: IVbTextTrack = this.TextTrackHelper.getTrack(this.vgApi.rawMediaElement, TextTrackKinds.CAPTIONS, '', false);

		if (track) {
			track.mode = isEnabled ? TextTrackModes.SHOWING : TextTrackModes.HIDDEN;
		}
	}

	private showSubtitles(): void {
		const src: string = (this.vgApi.activeSubtitle as ISubtitleOption).src;

		this.SubtitleService.getParsedSubtitle(src)
			.then(result => {
				this.subtitles = result;

				if (!this.vgApi.isHtmlSubtitleRenderingEnabled) {
					this.renderThroughTextTrack();
				}
			})
			.catch(err => console.error('Unable to load subtitles from ', err, src));
	}

	public onManualLangChange(subtitleOption: SubtitleType): void {
		this.isManualChanged = true;
		this.updateSelection(subtitleOption);
	}

	private onAutoForcedLangSelect(): void {
		if (this.isManualChanged || !this.activeSource || (!this.forcedCCOn && !this.langOrder?.length)) {
			return;
		}
		const activeSubtitle = this.getCCOrSubtitleByPreferanceOrder();

		if (activeSubtitle) {
			this.updateSelection(activeSubtitle);
		}
	}

	public updateSelection(subtitleOption: SubtitleType): void {
		this.captionsEnabled = subtitleOption === this.SUBTITLE_OPTION_CAPTIONS;
		this.isTextTrackType = (subtitleOption as TextTrack)?.kind === TextTrackKinds.SUBTITLES;

		if (this.isMobile || subtitleOption !== this.vgApi.activeSubtitle) { //change
			this.vgApi.setActiveSubtitle(subtitleOption);
			this.PlayerUserPreferencesService.setPreference(PLAYER_USER_PREFERENCE_KEY.SUBTITLE, subtitleOption);
			const textTracks: TextTrackList = this.vgApi.rawMediaElement.textTracks;

			//needed only for native case.
			if (userAgentUtil.isSafariIos && textTracks && textTracks.removeEventListener) {
				textTracks.removeEventListener('change', this.onTextTrackListChangeDebounce);
			}

			if (subtitleOption && !this.captionsEnabled && !this.isTextTrackType) {
				this.hideSubtitles();
				this.reevaluateSubtitleRenderingMethod();
				this.showSubtitles();
			} else if (this.isTextTrackType) {
				this.reevaluateSubtitleRenderingMethod();
				this.onUpdateTextTrackSelection(subtitleOption as TextTrack);
			} else {
				this.hideSubtitles();
			}

			this.setCaptionEnabled();

			//attach listener to handle native player control changes to the selection
			if (userAgentUtil.isSafariIos && textTracks && textTracks.addEventListener) {
				textTracks.addEventListener('change', this.onTextTrackListChangeDebounce);
			}
		}
	}

	private onUpdateTextTrackSelection(textTrack: TextTrack): void {
		this.TextTrackHelper.hideAllTracks(this.vgApi.rawMediaElement);
		this.selectedNativeTextTrack = this.TextTrackHelper.getTrack(this.vgApi.rawMediaElement, TextTrackKinds.SUBTITLES, textTrack.label);
		this.vgApi.setSubtitleTrackInHlsPlugin?.(!this.vgApi.isHtmlSubtitleRenderingEnabled, textTrack);

		if (!this.vgApi.isHtmlSubtitleRenderingEnabled) {
			this.selectedNativeTextTrack.mode = TextTrackModes.SHOWING;
		}
	}

	private getCCOrSubtitleByPreferanceOrder(): SubtitleType {
		if (!this.forcedCCOn) {
			return;
		}

		if (this.isCaptionsAvailable) {
			return SUBTITLE_OPTION_CAPTIONS;
		}

		if (!this.langOrder?.length || !this.subtitleOptionsInternal?.length) {
			return;
		}
		let activeSubtitle;

		for (const lang of this.langOrder) {
			//find exact matching.
			activeSubtitle = this.subtitleOptionsInternal.find(sub => lang === sub.language);

			if (!activeSubtitle) {
				//take the base part. lang may have hyphen as well;
				const langBasePart = lang.split('-')[0];
				for(const sub of this.subtitleOptionsInternal) {
					//check if lang exact base part is there.
					if (sub.language === langBasePart) {
						activeSubtitle = sub;
						break;
					} else if (sub.language.startsWith(langBasePart)) { //pick lang from the same group;
						activeSubtitle = sub;
					}
				}
			}

			if (activeSubtitle) {
				break;
			}
		}
		return activeSubtitle;
	}

	private setCaptionEnabled(): void {
		if (this.vgApi.setCaptionsEnabled) {
			this.vgApi.setCaptionsEnabled(this.captionsEnabled);
		} else if (this.isSafari) {
			this.setSafariCaptionsEnabled(this.captionsEnabled);
		}
	}
}
